import type { BlogPost } from './blogPosts';

/**
 * 30 articles (ids 51–80) — cibles dirigeant / DAF / commercial / ops.
 * Audit, insights, CA, patterns, entreprise.
 */
export const growthBlogPosts: BlogPost[] = [
  {
    id: "51",
    slug: "audit-donnees-entreprise-ia-pme",
    title: "Audit de vos données d’entreprise : ce que vos fichiers savent déjà (et que personne ne lit)",
    metaTitle: "Audit données entreprise IA | Clairify",
    metaDescription: "Audit de données d’entreprise pour PME : Excel, CRM, compta. Trouver les insights, les fuites de CA et les patterns. Méthode IA France.",
    excerpt: "Un audit data, ce n’est pas un projet SI de six mois. C’est lire ce que vos exports disent déjà — contradictions, clients qui pèsent, CA qui ment.",
    category: "Audit",
    readTime: 9,
    publishDate: "2026-08-21",
    author: { name: "Équipe Clairify", role: "Experts IA & Data" },
    keywords: ["audit données entreprise", "audit data PME", "audit Excel entreprise", "diagnostic données IA", "audit business intelligence"],
    content: {
      introduction: "La plupart des entreprises n’ont pas un problème de données. Elles ont un problème de lecture. CRM, caisse, compta, stock : tout est là. Personne ne croise. L’« audit data » vendu par les cabinets, c’est souvent un PowerPoint et un entrepôt. Trop tard, trop cher, trop loin du gérant.\n\nClairify pose un autre audit : importez ce que vous avez, laissez la mémoire indexer, lisez les collisions. En une semaine vous savez si le CA fête une promo qui tue la marge, si trois clients portent tout, si le pipeline commercial est un théâtre. Ce n’est pas un commissariat aux comptes. C’est un audit de clarté.",
      sections: [
    {
      title: "Ce qu’un audit data doit produire (et ce qu’il ne doit pas)",
      content: "Doit : une liste de tensions chiffrées, des fichiers sources nommés, un owner par tension, une date de revue. Ne doit pas : un schéma directeur, un ranking de maturité vaniteux, une reco « embauchez un data scientist ».\n\nSi le livrable n’est pas actionnable lundi, ce n’est pas un audit. C’est de la consultance. Clairify pousse le premier. Le Deep Report sert de pack d’audit que le dirigeant peut annoter.",
      keyPoints: ["Tensions", "Sources", "Owner", "Pas de schéma SI"],
    },
    {
      title: "Les cinq zones à auditer en premier",
      content: "Ventes (qui paie vraiment). Marge (ce que le CA cache). Cash / DSO. Stock ou charge de travail. Marketing ou acquisition si vous dépensez. Pas besoin des cinq le même jour. Une zone, deux fichiers, une collision.\n\nL’erreur classique : tout auditer d’un coup et n’en tirer aucune décision. Un audit étroit et tenu bat un diagnostic universel oublié.",
      keyPoints: ["Ventes", "Marge", "Cash", "Stock", "Acquisition"],
    },
    {
      title: "Méthode en 10 jours, sans équipe data",
      content: "J1 : choisir la zone. J2 : deux exports propres. J3 : import Clairify. J4–5 : lire collisions et patterns. J6 : owner + hypothèse. J10 : première revue. Le Memory Layer commence à compter au deuxième cycle, pas au PowerPoint J1.\n\nHébergement France, RGPD : un audit de fichiers clients n’est pas anodin. Ne le faites pas dans un chatbot US.",
    },
    {
      title: "Audit vs reporting : ne les confondez pas",
      content: "Le reporting dit « voici les KPI ». L’audit dit « voici ce qui s’annule ». Un dashboard vert peut cacher une entreprise qui se vide. C’est pour ça que Clairify refuse de n’être qu’un tableau de bord : l’audit est une phrase, pas un feu tricolore.",
    },
    {
      title: "Questions fréquentes",
      content: "Questions que se posent dirigeants, DAF et commerciaux après cet article. Réponses nettes.\n\nFaut-il un data warehouse ?\n\nNon. Des exports Excel/CSV suffisent pour un premier audit.\n\nQui doit piloter l’audit ?\n\nLe dirigeant ou le DAF. Pas uniquement un stagiaire.\n\nCombien de temps ?\n\nDix jours pour une zone. Pas un semestre.\n\nClairify remplace-t-il un cabinet ?\n\nNon. Il prépare la clarté. Un cabinet peut ensuite industrialiser. L’inverse est trop cher pour une PME.",
      keyPoints: ["Faut-il un data warehouse ? — Non. Des exports Excel/CSV suffisent pour un premier audit.", "Qui doit piloter l’audit ? — Le dirigeant ou le DAF. Pas uniquement un stagiaire.", "Combien de temps ? — Dix jours pour une zone. Pas un semestre.", "Clairify remplace-t-il un cabinet ? — Non. Il prépare la clarté. Un cabinet peut ensuite industrialiser. L’inverse est trop cher pour une PME."],
    }
      ],
      conclusion: "Auditer vos données, c’est accepter de voir ce que le CA ne dit pas. Clairify accélère cette lecture. Ensuite, c’est du management.",
      cta: {
        title: "Lancer un premier audit dans le studio",
        description: "Deux fichiers. Une zone. Gratuit, sans carte, Paris.",
      },
    },
  },
  {
    id: "52",
    slug: "diagnostic-entreprise-dirigeant-ia",
    title: "Diagnostic d’entreprise pour dirigeant : voir clair sans attendre le prochain commissaire",
    metaTitle: "Diagnostic entreprise dirigeant IA | Clairify",
    metaDescription: "Diagnostic d’entreprise pour dirigeant de PME : CA, marge, clients, cash. Une IA à mémoire pour un état des lieux actionnable, pas un rapport de 80 pages.",
    excerpt: "Le diagnostic que le dirigeant peut lire le dimanche soir. Pas un audit légal. Une photo nette de ce qui cloche dans les chiffres.",
    category: "Audit",
    readTime: 9,
    publishDate: "2026-08-22",
    author: { name: "Équipe Clairify", role: "Experts IA & Data" },
    keywords: ["diagnostic entreprise PME", "diagnostic dirigeant", "état des lieux entreprise IA", "santé entreprise data", "diagnostic business"],
    content: {
      introduction: "Le dirigeant de PME diagnostique au feeling : « ça tend », « le commercial râle », « la banque a appelé ». Les fichiers, eux, savent déjà. Ils ne parlent pas la langue du comité. Un diagnostic utile traduit : voici les trois tensions, voici ce qui a changé en 90 jours, voici ce qu’il faut trancher.\n\nClairify n’est pas un expert-comptable. Le DNA Score n’est pas une notation Banque de France. C’est un miroir d’usage et de clarté. Le diagnostic, ce sont les collisions et le Deep Report.",
      sections: [
    {
      title: "Les questions d’un diagnostic de dirigeant",
      content: "Qui sont les clients sans lesquels on meurt ? Quelle activité gonfle le CA et vide la caisse ? Qu’est-ce qui a changé vs N-1 sans qu’on l’ait nommé ? Où le commercial et le DAF ne racontent pas la même histoire ?\n\nSi vous ne pouvez pas répondre en dix minutes, vous n’avez pas un diagnostic. Vous avez des réunions.",
      keyPoints: ["Clients", "Cash vs CA", "N vs N-1", "Deux vérités"],
    },
    {
      title: "Ce que le feeling rate systématiquement",
      content: "La saisonnalité prise pour une tendance. Le gros client qui paie mal. La promo « qui marche » en volume. Le recrutement qui allonge le DSO. Rien de tout cela n’est visible dans un seul Excel ouvert le vendredi.\n\nLe Memory Layer sert à comparer les cycles. Un diagnostic one-shot est une photo. Un diagnostic mémorisé est une courbe.",
    },
    {
      title: "Format livrable : une page, pas un roman",
      content: "Trois tensions. Une reco par tension. Une limite (« on n’a pas le fichier X »). C’est le Deep Report. Si ça ne tient pas sur une page, ce n’est pas un diagnostic de dirigeant, c’est un mémoire d’école.",
    },
    {
      title: "Questions fréquentes",
      content: "Questions que se posent dirigeants, DAF et commerciaux après cet article. Réponses nettes.\n\nC’est un audit légal ?\n\nNon. Pilotage. La liasse reste chez l’expert-comptable.\n\nSeul le DG peut le faire ?\n\nLe DG doit le lire. Le DAF peut préparer les exports.\n\nTous les mois ?\n\nMensuel suffit. Hebdo seulement si le cash brûle.\n\nEt si tout va « bien » ?\n\nAlors le diagnostic le confirmera — ou montrera que « bien » était un CA sans marge.",
      keyPoints: ["C’est un audit légal ? — Non. Pilotage. La liasse reste chez l’expert-comptable.", "Seul le DG peut le faire ? — Le DG doit le lire. Le DAF peut préparer les exports.", "Tous les mois ? — Mensuel suffit. Hebdo seulement si le cash brûle.", "Et si tout va « bien » ? — Alors le diagnostic le confirmera — ou montrera que « bien » était un CA sans marge."],
    }
      ],
      conclusion: "Un dirigeant n’a pas besoin de plus de slides. Il a besoin d’un diagnostic qu’il ose poser sur la table. Clairify le prépare. Il tranche.",
      cta: {
        title: "Obtenir un premier diagnostic",
        description: "Importez ventes + marge ou ventes + banque. Lisez.",
      },
    },
  },
  {
    id: "53",
    slug: "augmenter-chiffre-affaires-insights-data",
    title: "Augmenter son chiffre d’affaires avec les insights (sans magie commerciale)",
    metaTitle: "Augmenter chiffre d’affaires insights data | Clairify",
    metaDescription: "Augmenter le CA d’une PME grâce aux insights : clients sous-exploités, mix, pricing, fuites. Méthode data, pas un script de closing.",
    excerpt: "Le CA ne se décrète pas. Il se lit : qui achète, à quel prix, ce qui fuit. Les insights sont là. Encore faut-il les clarifier.",
    category: "Croissance",
    readTime: 9,
    publishDate: "2026-08-23",
    author: { name: "Équipe Clairify", role: "Experts IA & Data" },
    keywords: ["augmenter chiffre d'affaires PME", "augmenter CA data", "insights ventes entreprise", "croissance CA insights", "opportunités chiffre d'affaires"],
    content: {
      introduction: "« Il faut augmenter le CA » est la phrase la plus vide d’un comité. Augmenter comment : plus de clients, plus de panier, moins de fuites, un meilleur prix, un mix moins suicidaire ? Chaque levier a un fichier. Presque aucun n’est lu jusqu’au bout.\n\nClairify ne ferme pas vos deals. Il montre où le CA se cache déjà — et où il se détruit. C’est plus utile qu’un énième challenge commercial.",
      sections: [
    {
      title: "Quatre leviers, quatre fichiers",
      content: "Acquisition (leads, dépenses). Conversion (pipeline). Expansion (panier, récurrence). Rétention / fuites (churn, retours, avoirs). Un insight de CA qui ne dit pas le levier est un slogan.\n\nCommencez par le levier le plus douloureux, pas le plus « data ». Importez deux sources. Cherchez la collision : volume vs marge, panier vs retours, nouveaux vs anciens.",
      keyPoints: ["Acquisition", "Conversion", "Expansion", "Fuites"],
    },
    {
      title: "Ce n’est pas « plus de prospection »",
      content: "Souvent le CA supplémentaire est déjà dans la base : clients qui n’ont pas racheté, SKU sous-tarifés, canaux qui rapportent après coût. La prospection est le levier le plus cher. Lisez d’abord.\n\nUn pattern Clairify typique : 20 % des clients font 80 % du CA — et 8 % du temps commercial. Réallouer une journée de terrain, c’est parfois plus de CA qu’une campagne.",
    },
    {
      title: "Marge : le CA qui ne compte pas",
      content: "Un CA qui monte via la promo ou le discount n’est pas une victoire. L’insight utile dit la contribution. Sans ça, vous « augmentez le CA » jusqu’à la cessation. Les Pattern Collisions existent pour cet instant précis.",
    },
    {
      title: "Questions fréquentes",
      content: "Questions que se posent dirigeants, DAF et commerciaux après cet article. Réponses nettes.\n\nEn combien de temps un insight CA ?\n\nSouvent dès le premier croisement propre.\n\nÇa remplace un CRM ?\n\nNon. Ça lit l’export CRM.\n\nEt le marketing ?\n\nUn levier parmi d’autres. Mesurez la contribution, pas le vanity.\n\nPromesse de +X % ?\n\nNon. Une lecture. Le % dépend de ce que vous tranchez.",
      keyPoints: ["En combien de temps un insight CA ? — Souvent dès le premier croisement propre.", "Ça remplace un CRM ? — Non. Ça lit l’export CRM.", "Et le marketing ? — Un levier parmi d’autres. Mesurez la contribution, pas le vanity.", "Promesse de +X % ? — Non. Une lecture. Le % dépend de ce que vous tranchez."],
    }
      ],
      conclusion: "Augmenter le CA, c’est d’abord arrêter de le compter bêtement. Clairify clarifie les leviers. Le commercial exécute. Dans cet ordre.",
      cta: {
        title: "Voir où votre CA se cache",
        description: "Export ventes + un second fichier (marge, retours ou CRM). Studio.",
      },
    },
  },
  {
    id: "54",
    slug: "insights-caches-fichiers-entreprise",
    title: "Insights cachés dans vos fichiers : comment les faire sortir (sans data scientist)",
    metaTitle: "Insights cachés fichiers entreprise | Clairify",
    metaDescription: "Trouver les insights cachés dans Excel, CRM et compta. Patterns, contradictions, opportunités de CA. Guide pour dirigeants de PME.",
    excerpt: "Vos fichiers sont pleins. Vos réunions sont vides. Voici comment extraire des insights actionnables — et ce qu’une IA à mémoire change.",
    category: "Insights",
    readTime: 9,
    publishDate: "2026-08-24",
    author: { name: "Équipe Clairify", role: "Experts IA & Data" },
    keywords: ["insights cachés données", "insights business PME", "trouver insights Excel", "analyse insights entreprise", "insights IA"],
    content: {
      introduction: "Un insight n’est pas un graphe. C’est une phrase qui change une décision : « ce canal coûte plus qu’il ne rapporte », « ce client est un risque cash », « cette promo revient chaque mars et détruit avril ». Ça vit déjà dans les fichiers. Ça meurt dans les copier-coller.\n\nClairify est bâti pour faire sortir ces phrases. Memory Layer pour le temps. Collisions pour les vérités qui s’annulent. Deep Report pour le lundi.",
      sections: [
    {
      title: "Insight vs vanity vs KPI",
      content: "Le KPI décrit. Le vanity flatte. L’insight tranche. « +12 % de trafic » n’est pas un insight. « Le trafic payant convertit 3× moins que l’organique après retours » en est un.\n\nSi votre outil ne produit que des KPI, vous n’avez pas d’insights. Vous avez un musée. Passez à une couche qui nomme.",
      keyPoints: ["KPI", "Vanity", "Décision"],
    },
    {
      title: "Où ils se cachent vraiment",
      content: "À l’intersection de deux fichiers, presque toujours. Ventes × marge. CRM × banque. Caisse × achats. Un seul export donne des totaux. Deux exports donnent une histoire.\n\nD’où le geste Clairify : importer, croiser, mémoriser. Pas « un prompt sur un CSV ».",
    },
    {
      title: "Comment les capter en équipe",
      content: "Un owner par insight. Une date. Tenir ou tuer. Sinon l’insight redevient une slide. Le studio n’assiste pas à votre stand-up. Il prépare la phrase. Vous l’assumez.",
    },
    {
      title: "Questions fréquentes",
      content: "Questions que se posent dirigeants, DAF et commerciaux après cet article. Réponses nettes.\n\nChatGPT peut-il sortir les insights ?\n\nParfois une phrase. Jamais une mémoire. Souvent un risque RGPD.\n\nCombien d’insights par mois ?\n\nAssez pour décider. Trop, c’est du bruit.\n\nFaut-il former l’équipe ?\n\nUn rituel d’une heure. Pas une certif data.\n\nEt si on n’a « rien » ?\n\nAlors vos fichiers sont trop sales ou trop étroits. Checklist qualité d’abord.",
      keyPoints: ["ChatGPT peut-il sortir les insights ? — Parfois une phrase. Jamais une mémoire. Souvent un risque RGPD.", "Combien d’insights par mois ? — Assez pour décider. Trop, c’est du bruit.", "Faut-il former l’équipe ? — Un rituel d’une heure. Pas une certif data.", "Et si on n’a « rien » ? — Alors vos fichiers sont trop sales ou trop étroits. Checklist qualité d’abord."],
    }
      ],
      conclusion: "Les insights ne manquent pas. La clarté manque. C’est le nom du produit.",
      cta: {
        title: "Faire sortir un premier insight",
        description: "Deux fichiers. Une question. Studio Clairify.",
      },
    },
  },
  {
    id: "55",
    slug: "detecter-patterns-business-ia-entreprise",
    title: "Détecter les patterns de votre business : saison, clients, promos, habitudes qui coûtent",
    metaTitle: "Détecter patterns business IA | Clairify",
    metaDescription: "Patterns business : saisonnalité, clients, mix, promos répétées. Comment une IA à mémoire les détecte dans une PME — sans data scientist.",
    excerpt: "Un pattern, c’est une habitude de l’entreprise. Certaines paient. D’autres vident la caisse. Encore faut-il les voir deux fois, pas une.",
    category: "Patterns",
    readTime: 9,
    publishDate: "2026-08-25",
    author: { name: "Équipe Clairify", role: "Experts IA & Data" },
    keywords: ["patterns business", "détecter patterns données", "patterns ventes PME", "analyse patterns IA", "habitudes entreprise data"],
    content: {
      introduction: "Le mot pattern est devenu du jargon. En français : une chose qui se répète. Tous les mars, cette famille de SKU. Tous les jeudis, ce client. Toutes les fins de trimestre, cette remise « exceptionnelle ». Excel le contient. Personne ne le nomme comme une habitude.\n\nLe Memory Layer de Clairify existe pour ça. Une photo ne fait pas un pattern. Trois cycles, si.",
      sections: [
    {
      title: "Patterns utiles vs folklore statistique",
      content: "Utile : répétable, actionnable, tenu par un owner. Folklore : une corrélation sur 40 lignes, un « mardi porte-bonheur ». Nous refusons le folklore. Une PME n’a pas assez de data pour jouer au data scientist de conférence.\n\nLe bon test : est-ce que je change quelque chose lundi ? Sinon ce n’est pas un pattern métier. C’est une anecdote.",
      keyPoints: ["Répétable", "Actionnable", "Owner"],
    },
    {
      title: "Les patterns qui coûtent le plus cher",
      content: "La promo rituelle. Le client « historique » sous-tarifé. Le pic que l’on stocke trop tôt. Le commercial qui pousse le mauvais mix. Aucun n’apparaît dans un KPI isolé. Tous apparaissent en collision + mémoire.",
    },
    {
      title: "Comment les documenter sans usine",
      content: "Une phrase, une période, un fichier source, une décision. Le studio mémorise. Vous n’ouvrez pas un wiki. Au cycle suivant, Clairify rappelle : « déjà vu ». C’est tout le produit.",
    },
    {
      title: "Questions fréquentes",
      content: "Questions que se posent dirigeants, DAF et commerciaux après cet article. Réponses nettes.\n\nDès le premier fichier ?\n\nDes totaux, oui. Un pattern solide, au 2e–3e cycle.\n\nC’est du machine learning ?\n\nC’est de la mémoire métier. Méfiez-vous du ML de brochure.\n\nOn peut se tromper de pattern ?\n\nOui si le fichier est sale. Qualité d’abord.\n\nLien avec les collisions ?\n\nLe pattern répète. La collision frotte deux vérités. Souvent ensemble.",
      keyPoints: ["Dès le premier fichier ? — Des totaux, oui. Un pattern solide, au 2e–3e cycle.", "C’est du machine learning ? — C’est de la mémoire métier. Méfiez-vous du ML de brochure.", "On peut se tromper de pattern ? — Oui si le fichier est sale. Qualité d’abord.", "Lien avec les collisions ? — Le pattern répète. La collision frotte deux vérités. Souvent ensemble."],
    }
      ],
      conclusion: "Détecter les patterns, c’est arrêter de traiter chaque mois comme une surprise. Clairify se souvient. Vous décidez si l’habitude survit.",
      cta: {
        title: "Laisser la mémoire voir vos habitudes",
        description: "Trois imports, même export. C’est le minimum d’un pattern.",
      },
    },
  },
  {
    id: "56",
    slug: "audit-commercial-pipeline-ventes-ia",
    title: "Audit commercial : ce que votre pipeline raconte (et ce qu’il invente)",
    metaTitle: "Audit commercial pipeline ventes IA | Clairify",
    metaDescription: "Audit commercial PME : pipeline, conversion, clients qui pèsent, relances. Lire le CRM avec une IA métier, pas un dashboard de plus.",
    excerpt: "Le pipeline est souvent un roman. L’audit commercial le ramène aux faits : qui signe, qui paie, qui stagne, qui coûte.",
    category: "Audit",
    readTime: 9,
    publishDate: "2026-08-26",
    author: { name: "Équipe Clairify", role: "Experts IA & Data" },
    keywords: ["audit commercial PME", "audit pipeline ventes", "analyse CRM IA", "conversion commerciale data", "performance commerciale insights"],
    content: {
      introduction: "Le directeur commercial montre un pipeline. Le DAF montre les encaissements. L’écart, c’est l’audit. Un CRM rempli d’optimisme n’est pas une prévision. C’est une culture. Clairify ne juge personne. Il croise l’export CRM et l’export factures.\n\nRésultat typique : 40 % du « forecast » n’a jamais payé à date. Ou 15 comptes font le trimestre. Ou le cycle s’allonge pendant que l’on recrute. Ce sont des insights de CA, pas des reproches.",
      sections: [
    {
      title: "Les pièces de l’audit commercial",
      content: "Étapes et durées. Taux de passage. Concentration clients. Écart forecast vs facturé. Mix produits poussés. Temps passé vs CA (si vous l’avez). Deux fichiers minimum : CRM + ventes/compta.\n\nSans le second, vous auditez un récit. Avec le second, vous auditez une entreprise.",
      keyPoints: ["Forecast vs réel", "Concentration", "Cycle"],
    },
    {
      title: "Ce que le commercial vit mal (et pourquoi il a raison de se méfier)",
      content: "Un outil qui « flique ». Ce n’est pas le but. Le but, c’est d’arrêter les réunions où tout le monde ment un peu. Un insight partagé (« ces cinq comptes portent tout ») protège l’équipe mieux qu’un challenge absurde.\n\nPrésentez Clairify comme un éclairage, pas un tribunal. Le Deep Report se lit ensemble.",
    },
    {
      title: "Après l’audit : trois décisions, pas vingt KPI",
      content: "Réallouer le temps. Tuer les deals zombies. Revoir le discount. C’est tout. Un audit commercial qui produit un dashboard de plus a échoué.",
    },
    {
      title: "Questions fréquentes",
      content: "Questions que se posent dirigeants, DAF et commerciaux après cet article. Réponses nettes.\n\nSalesforce, HubSpot, Pipedrive ?\n\nL’export compte, pas la marque.\n\nLes commerciaux vont-ils jouer le jeu ?\n\nS’il n’y a pas de tribunal, oui. Un owner dirco, pas un flicage RH.\n\nFréquence ?\n\nMensuel. Pas quotidien.\n\nLien cash ?\n\nToujours. Un deal « gagné » non encaissé n’est pas gagné.",
      keyPoints: ["Salesforce, HubSpot, Pipedrive ? — L’export compte, pas la marque.", "Les commerciaux vont-ils jouer le jeu ? — S’il n’y a pas de tribunal, oui. Un owner dirco, pas un flicage RH.", "Fréquence ? — Mensuel. Pas quotidien.", "Lien cash ? — Toujours. Un deal « gagné » non encaissé n’est pas gagné."],
    }
      ],
      conclusion: "Le pipeline n’est pas le CA. L’audit, c’est cette phrase. Clairify la documente. Le dirco l’assume.",
      cta: {
        title: "Auditer le pipeline",
        description: "Export CRM + export factures. Une semaine.",
      },
    },
  },
  {
    id: "57",
    slug: "audit-marge-rentabilite-entreprise-ia",
    title: "Audit de marge : pourquoi votre CA peut monter pendant que l’entreprise s’appauvrit",
    metaTitle: "Audit marge rentabilité entreprise IA | Clairify",
    metaDescription: "Audit de marge PME : CA vs contribution, promos, mix, clients ruinaux. Comment une IA détecte la rentabilité réelle.",
    excerpt: "Le chiffre d’affaires n’est pas la santé. L’audit de marge est l’audit que tout le monde recule — et le plus rentable.",
    category: "Audit",
    readTime: 9,
    publishDate: "2026-08-27",
    author: { name: "Équipe Clairify", role: "Experts IA & Data" },
    keywords: ["audit marge PME", "rentabilité entreprise data", "CA vs marge", "analyse rentabilité clients", "contribution marge IA"],
    content: {
      introduction: "On fête le CA. On enterre la marge dans un onglet. Puis la trésorerie râle. L’audit de marge consiste à dire : quelle activité, quel client, quelle promo, quel canal contribuent vraiment. Pas le taux de marque du devis. Le réel.\n\nClairify croise ventes et coûts (ou marge déjà calculée). Les collisions promo/volume sont le cas d’école. Retail, e-commerce, BTP, agence : le mécanisme est le même.",
      sections: [
    {
      title: "Marge commerciale vs marge nette vs cash",
      content: "Trois étages. Les confondre, c’est piloter un mythe. Un client « rentable » qui paie à 90 jours peut tuer. Un SKU à forte marge mais retourné à 30 % aussi.\n\nL’insight doit préciser l’étage. Sinon le commercial et le DAF se battent pour de bonnes raisons.",
      keyPoints: ["Commerciale", "Nette", "Cash"],
    },
    {
      title: "Où la marge fuit",
      content: "Remises « exceptionnelles » devenues habitude. Mix poussé par l’incentive. Frais logistiques oubliés. Heures non facturées. Avoirs. Choisissez deux fuites, pas dix. Auditez.",
    },
    {
      title: "Livrable",
      content: "Une liste ABC de contribution. Trois collisions. Une règle (seuil de remise, SKU intouchable). Revue à 15 jours. Le Memory Layer dira si la règle tient.",
    },
    {
      title: "Questions fréquentes",
      content: "Questions que se posent dirigeants, DAF et commerciaux après cet article. Réponses nettes.\n\nOn n’a pas le coût de revient ?\n\nAlors auditez remise, mix et avoirs. Déjà énorme.\n\nC’est de la compta analytique ?\n\nC’est du pilotage. L’analytique peut suivre.\n\nTous les clients à nu ?\n\nAgrégats si RGPD. B2B sociétés : plus simple.\n\nRisque de braquer les ventes ?\n\nUn seuil partagé calme plus qu’un silence.",
      keyPoints: ["On n’a pas le coût de revient ? — Alors auditez remise, mix et avoirs. Déjà énorme.", "C’est de la compta analytique ? — C’est du pilotage. L’analytique peut suivre.", "Tous les clients à nu ? — Agrégats si RGPD. B2B sociétés : plus simple.", "Risque de braquer les ventes ? — Un seuil partagé calme plus qu’un silence."],
    }
      ],
      conclusion: "Auditer la marge, c’est accepter que le CA puisse mentir. Clairify écrit le mensonge. Vous changez la règle — ou vous assumez.",
      cta: {
        title: "Croiser ventes et marge",
        description: "Deux exports. Une collision. C’est l’audit.",
      },
    },
  },
  {
    id: "58",
    slug: "augmenter-insights-equipe-direction",
    title: "Augmenter les insights de votre équipe de direction (sans embaucher un analyste)",
    metaTitle: "Augmenter insights équipe direction | Clairify",
    metaDescription: "Plus d’insights pour COMEX et CODIR : rituel, fichiers, IA à mémoire. Comment une PME augmente la qualité des décisions, pas le volume de slides.",
    excerpt: "Augmenter les insights, ce n’est pas produire plus de rapports. C’est en produire moins, plus vrais, tenus par un nom.",
    category: "Insights",
    readTime: 9,
    publishDate: "2026-08-28",
    author: { name: "Équipe Clairify", role: "Experts IA & Data" },
    keywords: ["augmenter insights entreprise", "insights direction PME", "COMEX insights data", "équipe data driven", "qualité décisions data"],
    content: {
      introduction: "Les équipes de direction se noient dans l’information et manquent d’insights. La différence, c’est l’action. Un pack de 40 pages n’augmente rien. Une collision avec owner, si.\n\nClairify est un multiplicateur d’insights à condition d’un rituel. Sans rituel, c’est un chat de plus. Avec rituel, le CODIR parle enfin la même langue.",
      sections: [
    {
      title: "La règle des trois insights",
      content: "Pas plus de trois tensions ouvertes à la fois. Au-delà, personne ne décide. Augmenter la qualité, pas le stock. Le Deep Report doit tenir sur une page de comité.",
      keyPoints: ["3 tensions max", "Owner", "Date"],
    },
    {
      title: "Qui produit, qui tranche",
      content: "Le DAF ou l’ops prépare les exports. L’IA clarifie. Le dirigeant tranche. Si le dirigeant « n’a pas le temps », les insights mourront. Ce n’est pas un problème d’outil.",
    },
    {
      title: "Mesurer si ça augmente vraiment",
      content: "Pas le nombre de graphes. Le nombre de décisions tenues à 15 jours. Le DNA Score comme tendance de clarté, pas comme médaille. Si rien n’est tranché, vous n’avez pas augmenté les insights. Vous avez augmenté le papier.",
    },
    {
      title: "Questions fréquentes",
      content: "Questions que se posent dirigeants, DAF et commerciaux après cet article. Réponses nettes.\n\nFaut-il un data analyst ?\n\nNon pour commencer. Un owner d’exports, oui.\n\nHebdo ou mensuel ?\n\nMensuel. Hebdo si crise de cash.\n\nTous les n-1 dans la boucle ?\n\nNon. Ceux qui tranchent. Les autres reçoivent la page.\n\nOutil déjà en place (BI) ?\n\nGardez-le pour le figé. Insights ici.",
      keyPoints: ["Faut-il un data analyst ? — Non pour commencer. Un owner d’exports, oui.", "Hebdo ou mensuel ? — Mensuel. Hebdo si crise de cash.", "Tous les n-1 dans la boucle ? — Non. Ceux qui tranchent. Les autres reçoivent la page.", "Outil déjà en place (BI) ? — Gardez-le pour le figé. Insights ici."],
    }
      ],
      conclusion: "Plus d’insights, c’est plus de décisions. Clairify prépare. L’équipe assume. C’est tout le contrat.",
      cta: {
        title: "Installer le rituel d’insights",
        description: "Un Deep Report, trois tensions, une heure de CODIR.",
      },
    },
  },
  {
    id: "59",
    slug: "ia-analyse-donnees-entreprise-sans-analyste",
    title: "IA pour analyser les données de l’entreprise — sans data analyst ni DAX",
    metaTitle: "IA analyse données entreprise sans analyste | Clairify",
    metaDescription: "Analyser les données d’entreprise avec une IA : PME et ETI sans équipe data. Excel, insights, audit, patterns. Alternative aux projets BI.",
    excerpt: "Vous n’embauchez pas un analyste cette année. Vous avez des fichiers. Voici comment une IA métier les lit quand même.",
    category: "Entreprise",
    readTime: 9,
    publishDate: "2026-08-29",
    author: { name: "Équipe Clairify", role: "Experts IA & Data" },
    keywords: ["IA analyse données entreprise", "IA sans data analyst", "outil IA entreprise PME", "analyser données entreprise", "IA métier PME"],
    content: {
      introduction: "L’offre d’emploi « data analyst » à 45 k€ n’est pas un plan. Pour une société de 15 à 80 personnes, c’est souvent un mirage : recrutement, rampe, turnover, modèle qui meurt. L’alternative réaliste 2026 : une IA qui lit les exports, se souvient, nomme les collisions.\n\nCe n’est pas ChatGPT collé sur un CSV. C’est Clairify : Memory Layer, agents, France. Le dirigeant n’apprend pas le DAX. Il apprend un rituel d’import.",
      sections: [
    {
      title: "Ce que « sans analyste » ne veut pas dire",
      content: "Ça ne veut pas dire « sans cerveau ». Quelqu’un choisit les fichiers, lit les tensions, tranche. L’IA n’est pas le DAF. Elle évite que le DAF passe ses vendredis à recoller des TCD.",
      keyPoints: ["Owner humain", "Exports", "Décision"],
    },
    {
      title: "Pile minimale",
      content: "Excel/CSV. Un studio. Un rituel. Éventuellement la compta cloud pour l’export. Pas de data warehouse. Pas de certif Power BI. Si plus tard vous embauchez, tant mieux : la mémoire sera déjà là.",
    },
    {
      title: "Limites honnêtes",
      content: "Pas de live millions de lignes. Pas de pricing algorithmique. Pas de remplacement d’un contrôle de gestion structuré dans une ETI de 400 personnes avec SI unique. Pour le reste du tissu français, c’est largement assez.",
    },
    {
      title: "Questions fréquentes",
      content: "Questions que se posent dirigeants, DAF et commerciaux après cet article. Réponses nettes.\n\nUn stagiaire suffit ?\n\nPour l’import, oui. Pour trancher, non.\n\nFormation ?\n\nUne heure + le guide Excel du journal.\n\nEt dans 2 ans si on scale ?\n\nVous aurez une mémoire. Un analyste s’en servira. Vous n’aurez pas perdu deux ans.\n\nSécurité ?\n\nScaleway Paris, RGPD, isolation.",
      keyPoints: ["Un stagiaire suffit ? — Pour l’import, oui. Pour trancher, non.", "Formation ? — Une heure + le guide Excel du journal.", "Et dans 2 ans si on scale ? — Vous aurez une mémoire. Un analyste s’en servira. Vous n’aurez pas perdu deux ans.", "Sécurité ? — Scaleway Paris, RGPD, isolation."],
    }
      ],
      conclusion: "Sans analyste, vous pouvez quand même voir clair. À condition d’un outil qui se souvient. C’est Clairify.",
      cta: {
        title: "Analyser sans embaucher",
        description: "50 000 tokens. Sans carte. Vos fichiers.",
      },
    },
  },
  {
    id: "60",
    slug: "logiciel-ia-entreprise-pme-eti-2026",
    title: "Logiciel d’IA pour l’entreprise en 2026 : comment choisir (PME / ETI)",
    metaTitle: "Logiciel IA entreprise PME ETI 2026 | Clairify",
    metaDescription: "Choisir un logiciel d’IA en entreprise : mémoire, RGPD, insights, audit, CA. Grille pour PME et ETI françaises, sans se tromper de chatbot.",
    excerpt: "Le marché est plein d’« IA entreprise ». Presque tout est un chat. Voici une grille pour acheter un outil de clarté.",
    category: "Entreprise",
    readTime: 9,
    publishDate: "2026-08-30",
    author: { name: "Équipe Clairify", role: "Experts IA & Data" },
    keywords: ["logiciel IA entreprise", "outil IA PME", "IA entreprise France", "choisir IA métier", "plateforme IA données entreprise"],
    content: {
      introduction: "« On veut de l’IA » n’est pas un cahier des charges. Voulez-vous rédiger plus vite, coder plus vite, ou voir clair dans le business ? Clairify ne fait que le troisième. Si vous mélangez les trois dans un seul achat, vous achèterez un gadget.\n\nGrille 2026 : juridiction, mémoire, collisions, temps-to-insight, coût en cerveaux, essai sur VOS fichiers.",
      sections: [
    {
      title: "Chat vs studio métier",
      content: "Le chat produit du langage. Le studio produit une mémoire et des reco chiffrées. Microsoft Copilot, ChatGPT Enterprise, Gemini : rayon productivité. Clairify : rayon pilotage. Les deux peuvent coexister. Ils ne se remplacent pas.",
      keyPoints: ["Productivité", "Pilotage", "Coexistence"],
    },
    {
      title: "Critères qui éliminent un éditeur",
      content: "Entraînement occulte sur votre corpus. Hébergement flou. Pas d’effacement. Démo sur un dataset jouet. Promesse de prévision à 98 %. Si l’un de ces drapeaux est levé, passez votre chemin.",
    },
    {
      title: "Essai en 14 jours",
      content: "Un export réel. Une collision utile ou non. Un Deep Report lisible ou non. Un DAF qui revient ou non. C’est le seul RFP qui compte pour une PME. Les grilles de 40 critères sont pour les grands comptes.",
    },
    {
      title: "Questions fréquentes",
      content: "Questions que se posent dirigeants, DAF et commerciaux après cet article. Réponses nettes.\n\nPME ou ETI ?\n\nPME sans data team : cœur de cible. ETI : couche décision, pas remplacement du SI.\n\nAppel d’offres public ?\n\nSouveraineté France = argument. Scaleway.\n\nSSO, SOC2 ?\n\nDemandez selon votre taille. Le minimum : DPA et localisation.\n\nPrix ?\n\nGratuit 50k, Pro 29 €, Business 79 €, Enterprise 199 €.",
      keyPoints: ["PME ou ETI ? — PME sans data team : cœur de cible. ETI : couche décision, pas remplacement du SI.", "Appel d’offres public ? — Souveraineté France = argument. Scaleway.", "SSO, SOC2 ? — Demandez selon votre taille. Le minimum : DPA et localisation.", "Prix ? — Gratuit 50k, Pro 29 €, Business 79 €, Enterprise 199 €."],
    }
      ],
      conclusion: "Un logiciel d’IA d’entreprise se juge à la clarté du lundi, pas au communiqué. Clairify accepte ce jugement.",
      cta: {
        title: "Tester sur vos fichiers",
        description: "Pas de dataset jouet. Le vôtre.",
      },
    },
  },
  {
    id: "61",
    slug: "croissance-entreprise-decisions-data",
    title: "Croissance d’entreprise : ce que les décisions data changent (et ce qu’elles ne changent pas)",
    metaTitle: "Croissance entreprise décisions data | Clairify",
    metaDescription: "Croissance PME : insights, audit de CA, patterns. Ce que la data change vraiment — recrutement, offre, pricing — sans théâtre prédictif.",
    excerpt: "La croissance n’est pas un dashboard. C’est une série de paris. La data sert à tuer les mauvais plus tôt.",
    category: "Croissance",
    readTime: 9,
    publishDate: "2026-08-31",
    author: { name: "Équipe Clairify", role: "Experts IA & Data" },
    keywords: ["croissance entreprise PME", "croissance data driven", "décisions data croissance", "scaler PME insights", "développement CA data"],
    content: {
      introduction: "On associe data et croissance comme si un graphe faisait signer. Faux. La croissance vient d’une offre, d’un canal, d’une équipe. La data évite de scaler une promo ruineuse, un client unique, un prix trop bas.\n\nClairify accélère ces arbitrages. Ce n’est pas un moteur de croissance. C’est un phare. Les entreprises qui grandissent mal ont souvent trop de CA et trop peu de clarté.",
      sections: [
    {
      title: "Scaler ce qui contribue, pas ce qui s’affiche",
      content: "Un canal vanity, un logo client, un volume. La contribution après coûts et après cash. Insights de marge avant de recruter trois commerciaux.",
      keyPoints: ["Contribution", "Cash", "Capacité"],
    },
    {
      title: "Les paris à tuer vite",
      content: "Une offre qui « marche » en CA et meurt en marge. Une géographie. Un segment. 15 jours, owner, Memory Layer. Tuer un pari n’est pas un échec. C’est de la croissance adulte.",
    },
    {
      title: "Ce que la data ne remplace pas",
      content: "Le goût client, le produit, le courage commercial. Si votre offre est molle, Clairify le dira plus vite. Il ne la rendra pas désirée.",
    },
    {
      title: "Questions fréquentes",
      content: "Questions que se posent dirigeants, DAF et commerciaux après cet article. Réponses nettes.\n\nOn veut grandir vite ?\n\nAlors clarifiez encore plus. La vitesse sans lecture, c’est de la dette.\n\nLevée de fonds ?\n\nUn pack honnête bat un dashboard maquillé. Les investisseurs le sentent.\n\nFranchise / multi-sites ?\n\nLa mémoire par site est un cas d’école.\n\nB2B vs B2C ?\n\nMêmes collisions, fichiers différents.",
      keyPoints: ["On veut grandir vite ? — Alors clarifiez encore plus. La vitesse sans lecture, c’est de la dette.", "Levée de fonds ? — Un pack honnête bat un dashboard maquillé. Les investisseurs le sentent.", "Franchise / multi-sites ? — La mémoire par site est un cas d’école.", "B2B vs B2C ? — Mêmes collisions, fichiers différents."],
    }
      ],
      conclusion: "La croissance claire est plus lente en slides, plus nette en cash. C’est le genre de croissance que l’on vise.",
      cta: {
        title: "Éclairer le prochain pari",
        description: "Importez le canal ou l’offre que vous voulez scaler.",
      },
    },
  },
  {
    id: "62",
    slug: "opportunites-chiffre-affaires-fichiers-pme",
    title: "Trouver des opportunités de chiffre d’affaires dans vos fichiers (déjà là)",
    metaTitle: "Opportunités chiffre d’affaires fichiers | Clairify",
    metaDescription: "Opportunités de CA cachées : réachat, sous-tarification, mix, relances. Comment les extraire d’Excel et du CRM avec une IA.",
    excerpt: "Avant d’acheter plus de leads, ouvrez la base. Une partie du CA futur est déjà payée en acquisition. Encore faut-il la voir.",
    category: "Croissance",
    readTime: 9,
    publishDate: "2026-09-01",
    author: { name: "Équipe Clairify", role: "Experts IA & Data" },
    keywords: ["opportunités chiffre d'affaires", "trouver CA caché", "réachat clients data", "upsell PME insights", "relance commerciale data"],
    content: {
      introduction: "L’acquisition est un réflexe. L’expansion est un fichier. Clients inactifs à forte contribution historique. SKU jamais proposés ensemble. Zones où vous êtes trop bon marché. Relances jamais faites. Rien de tout cela n’exige une nouvelle campagne LinkedIn.\n\nClairify croise historique de ventes et (si vous l’avez) activité CRM. Un pattern de réachat cassé est une opportunité. Une collision prix/volume aussi — dans l’autre sens.",
      sections: [
    {
      title: "Cartographier sans CRM parfait",
      content: "Un export ventes avec client, date, montant, famille. Déjà suffisant pour dire qui s’est arrêté, qui a ralenti, qui a changé de mix. Le CRM ajoute l’intention. Il n’est pas obligatoire pour le premier passage.",
      keyPoints: ["Inactifs", "Ralentis", "Mix"],
    },
    {
      title: "Prioriser comme un DAF, pas comme un commercial excité",
      content: "Contribution × facilité × cash. Un petit client bruyant n’est pas une opportunité. Un silencieux qui payait bien, si. L’insight doit trier.",
    },
    {
      title: "Transformer en action",
      content: "Liste courte. Owner commercial. Message. Date. Réimporter. La mémoire dira qui a répondu. Sinon vous aurez fait un export pour rien.",
    },
    {
      title: "Questions fréquentes",
      content: "Questions que se posent dirigeants, DAF et commerciaux après cet article. Réponses nettes.\n\nRGPD relance B2C ?\n\nBase légale, opt-out. B2B contacts : cadre à respecter aussi.\n\nCombien de noms ?\n\nAssez pour une semaine de relances. Pas 4 000 lignes.\n\nUpsell vs simple rappel ?\n\nLe fichier dit le mix manquant. Le commercial dit le mot.\n\nEt les devis perdus ?\n\nSi vous les exportez, oui. Sinon commencez par les clients déjà facturés.",
      keyPoints: ["RGPD relance B2C ? — Base légale, opt-out. B2B contacts : cadre à respecter aussi.", "Combien de noms ? — Assez pour une semaine de relances. Pas 4 000 lignes.", "Upsell vs simple rappel ? — Le fichier dit le mix manquant. Le commercial dit le mot.", "Et les devis perdus ? — Si vous les exportez, oui. Sinon commencez par les clients déjà facturés."],
    }
      ],
      conclusion: "Les opportunités de CA sont rarement dans une pub. Elles sont dans l’historique. Clairify le lit. Vous rappelez.",
      cta: {
        title: "Sortir une liste d’opportunités",
        description: "Historique ventes. Une heure. Une liste.",
      },
    },
  },
  {
    id: "63",
    slug: "rentabilite-par-client-analyse-ia",
    title: "Rentabilité par client : qui vous fait vivre, qui vous épuise",
    metaTitle: "Rentabilité par client IA | Clairify",
    metaDescription: "Analyser la rentabilité par client : CA, marge, DSO, charge. Insights pour arrêter de traiter tous les comptes pareil.",
    excerpt: "Tous les clients ne se valent pas. Vos fichiers le savent. Votre organisation, souvent non.",
    category: "Pilotage",
    readTime: 9,
    publishDate: "2026-09-02",
    author: { name: "Équipe Clairify", role: "Experts IA & Data" },
    keywords: ["rentabilité par client", "analyser clients PME", "ABC clients marge", "clients rentables data", "portefeuille clients IA"],
    content: {
      introduction: "Le mythe de « le client est roi » ruine des PME. Certains rois coûtent plus qu’ils ne rapportent : remises, SAV, DSO, spécifications. Un insight de rentabilité par client n’est pas une insulte. C’est une allocation de temps.\n\nClairify agrège ce que vous avez : CA, marge si elle existe, retards de paiement, volume de lignes. Pas besoin d’un ABC parfait au centime près pour décider d’un service différencié.",
      sections: [
    {
      title: "Les quatre axes",
      content: "Contribution. Cash (DSO). Charge (tickets, spécifications — même qualitatives). Stratégie (référence, volume d’entrée). Un client stratégique déficitaire, c’est un choix. Un client déficitaire par habitude, c’est une fuite.",
      keyPoints: ["Contribution", "DSO", "Charge", "Stratégie"],
    },
    {
      title: "Ce qu’on fait des « mauvais »",
      content: "Prix, conditions, service, ou sortie polie. Pas un mail de rupture le lundi. Une règle. L’IA nomme. Le dirigeant assume le coup de fil.",
    },
    {
      title: "Portfolio, pas une liste de honte",
      content: "Le livrable n’est pas un ranking humiliant. C’est trois cohortes et une politique. Le commercial peut vivre avec une politique. Pas avec un tribunal.",
    },
    {
      title: "Questions fréquentes",
      content: "Questions que se posent dirigeants, DAF et commerciaux après cet article. Réponses nettes.\n\nB2C milliers de tickets ?\n\nAgrégats, pas nominatif. Cohortes.\n\nUn gros client toxique ?\n\nC’est le cas le plus politique. L’insight aide à oser.\n\nDonnées incomplètes ?\n\nClassez sur CA + DSO déjà. La marge viendra.\n\nPartage interne ?\n\nCercle restreint. Pas un Slack de 80 personnes.",
      keyPoints: ["B2C milliers de tickets ? — Agrégats, pas nominatif. Cohortes.", "Un gros client toxique ? — C’est le cas le plus politique. L’insight aide à oser.", "Données incomplètes ? — Classez sur CA + DSO déjà. La marge viendra.", "Partage interne ? — Cercle restreint. Pas un Slack de 80 personnes."],
    }
      ],
      conclusion: "Savoir qui vous fait vivre, c’est le début du vrai CA. Clairify classe. Vous politisez — au bon sens.",
      cta: {
        title: "Voir le poids réel des clients",
        description: "Export ventes (et paiements si possible).",
      },
    },
  },
  {
    id: "64",
    slug: "prix-trop-bas-detecter-data-entreprise",
    title: "Vos prix sont trop bas : comment le voir dans les données (avant de « faire une promo »)",
    metaTitle: "Prix trop bas détecter data | Clairify",
    metaDescription: "Détecter un sous-pricing : volume vs marge, remise chronique, mix. Insights pricing pour PME sans logiciel d’optimisation de prix.",
    excerpt: "La promo est souvent la réponse à un prix déjà trop bas. Les fichiers le montrent. Encore faut-il oser les lire.",
    category: "Croissance",
    readTime: 9,
    publishDate: "2026-09-03",
    author: { name: "Équipe Clairify", role: "Experts IA & Data" },
    keywords: ["prix trop bas entreprise", "analyse pricing PME", "remise chronique data", "optimisation prix insights", "sous-tarification"],
    content: {
      introduction: "Le pricing est un tabou français. On a peur de perdre le volume. On brade. Le CA tient. La marge non. Un insight pricing n’est pas un algorithme dynamiques de marketplace. C’est : cette famille, ce client, ce canal, cette remise devenue le prix.\n\nClairify ne fixe pas vos tarifs. Il montre où le volume n’a plus de sens. Collision classique : unités en hausse, contribution en baisse.",
      sections: [
    {
      title: "Signaux de sous-prix",
      content: "Remise moyenne qui dérive. « Exception » plus de 20 % des lignes. Clients historiques jamais révisés. Appels d’offres gagnés trop facilement. Mix qui glisse vers l’entrée de gamme.",
      keyPoints: ["Remise", "Exceptions", "Historique"],
    },
    {
      title: "Ce qu’il ne faut pas faire",
      content: "Augmenter tout le monde de 8 % lundi. Choisir un segment, une règle, un message. Tester. Réimporter. Mémoire.",
    },
    {
      title: "Promo vs prix",
      content: "Si la promo est le prix, vous n’avez plus de promo. Vous avez un problème d’offre. L’audit de marge et l’insight pricing sont le même dossier.",
    },
    {
      title: "Questions fréquentes",
      content: "Questions que se posent dirigeants, DAF et commerciaux après cet article. Réponses nettes.\n\nSans colonne remise ?\n\nPrix unitaire dans le temps × mix. Déjà parlant.\n\nConcurrence qui brade ?\n\nAlors segmentez. Tout le monde n’a pas à suivre.\n\nContrats cadres ?\n\nDatez les révisions. Un pattern annuel.\n\nB2C promo permanente ?\n\nC’est le cas e-commerce. Contribution après promo, pas avant.",
      keyPoints: ["Sans colonne remise ? — Prix unitaire dans le temps × mix. Déjà parlant.", "Concurrence qui brade ? — Alors segmentez. Tout le monde n’a pas à suivre.", "Contrats cadres ? — Datez les révisions. Un pattern annuel.", "B2C promo permanente ? — C’est le cas e-commerce. Contribution après promo, pas avant."],
    }
      ],
      conclusion: "Voir que le prix est trop bas, c’est déjà augmenter le CA futur — ou arrêter de le détruire. Clairify éclaire. Le barème, c’est vous.",
      cta: {
        title: "Repérer la dérive des remises",
        description: "Export lignes de ventes avec remise si vous l’avez.",
      },
    },
  },
  {
    id: "65",
    slug: "dso-recouvrement-insights-daf-ia",
    title: "DSO et recouvrement : les insights que le DAF voit trop tard",
    metaTitle: "DSO recouvrement insights DAF | Clairify",
    metaDescription: "DSO, retards, concentration du risque client. Comment une IA aide le DAF à voir le cash derrière le CA — sans remplacer la compta.",
    excerpt: "Le CA signé n’est pas le cash. Les insights de recouvrement évitent de fêter un trimestre déjà parti chez le client.",
    category: "Pilotage",
    readTime: 9,
    publishDate: "2026-09-04",
    author: { name: "Équipe Clairify", role: "Experts IA & Data" },
    keywords: ["DSO PME", "recouvrement insights", "retards paiement data", "cash vs CA", "analyse créances DAF"],
    content: {
      introduction: "Le DAF connaît le DSO global. Il connaît moins la dérive par client, par commercial, par offre. Un insight utile : tel segment allonge, tel logo « stratégique » est un risque, tel pic de CA de juin n’est pas encaissé en septembre.\n\nClairify lit l’échéancier ou l’export créances + ventes. Ce n’est pas un logiciel de recouvrement. C’est la clarté avant la relance.",
      sections: [
    {
      title: "CA, créances, encaissements",
      content: "Trois fichiers ou trois onglets. Les coller mentalement, c’est le job du DAF à 23 h. Les coller dans le studio, c’est une collision écrite pour le lundi.",
      keyPoints: ["Facturé", "Dû", "Encaissé"],
    },
    {
      title: "Concentration du risque",
      content: "Un client à 22 % du CA et 45 % des créances. Phrase de COMEX. Politique d’acompte. L’IA la calcule. Vous passez le coup de fil.",
    },
    {
      title: "Lien commercial",
      content: "Un incentive sur le signé sans le cash produit exactement ce DSO. L’insight doit arriver au dirco, pas seulement au DAF. Une page, pas une guerre.",
    },
    {
      title: "Questions fréquentes",
      content: "Questions que se posent dirigeants, DAF et commerciaux après cet article. Réponses nettes.\n\nRemplace-t-il le recouvrement ?\n\nNon. Il priorise.\n\nDonnées banque ?\n\nUtile. Pas obligatoire au premier passage.\n\nClients publics / 60 jours ?\n\nLe pattern compte, pas le moralisme. Contractualisez.\n\nPetit volume de factures ?\n\nEncore plus simple. Faites-le.",
      keyPoints: ["Remplace-t-il le recouvrement ? — Non. Il priorise.", "Données banque ? — Utile. Pas obligatoire au premier passage.", "Clients publics / 60 jours ? — Le pattern compte, pas le moralisme. Contractualisez.", "Petit volume de factures ? — Encore plus simple. Faites-le."],
    }
      ],
      conclusion: "Le recouvrement est une opération. L’insight est une politique. Clairify sert la seconde. Pennylane (ou autre) sert la première.",
      cta: {
        title: "Croiser ventes et créances",
        description: "Export lettrage / balance âgée + CA.",
      },
    },
  },
  {
    id: "66",
    slug: "saisonnalite-chiffre-affaires-patterns-ia",
    title: "Saisonnalité du CA : arrêter de confondre un pic et une tendance",
    metaTitle: "Saisonnalité chiffre d’affaires patterns | Clairify",
    metaDescription: "Saisonnalité des ventes : comparer N vs N-1, pics, stock. Patterns pour PME retail, e-commerce, services. Sans fausse prévision.",
    excerpt: "Juillet n’est pas décembre. Pourtant on pilote comme si chaque mois était une surprise. La mémoire existe pour ça.",
    category: "Patterns",
    readTime: 9,
    publishDate: "2026-09-05",
    author: { name: "Équipe Clairify", role: "Experts IA & Data" },
    keywords: ["saisonnalité ventes", "saisonnalité CA PME", "comparer ventes N-1", "patterns saisonniers IA", "pic vs tendance"],
    content: {
      introduction: "La saisonnalité est le pattern le plus évident et le plus mal lu. On surstocke. On sous-staffe. On fête un mois qui était déjà écrit l’an passé. Un insight saisonnier, c’est une comparaison honnête : même période, même périmètre, mémoire.\n\nClairify refuse les prévisions à 98 %. Il clarifie le déjà-vu. Retail, resto, BTP, agence : le calendrier ment si on le lit mois isolé.",
      sections: [
    {
      title: "Comparer ce qui est comparable",
      content: "Décembre vs décembre. Promo vs promo. Jours ouvrés. Périmètre magasin / canal. Si vous mélangez, vous inventez une croissance. L’IA honnête le signale. L’IA bavarde lisse.",
      keyPoints: ["N vs N-1", "Périmètre", "Ouvrés"],
    },
    {
      title: "Décisions de saison",
      content: "Stock, recrutement saisonnier, trésorerie, pub. Chacune a un fichier. La collision classique : CA de saison et marge bradée. Auditez avant de « remettre la même opération ».",
    },
    {
      title: "Deux saisons suffisent à commencer",
      content: "Pas besoin de dix ans. Deux cycles + Memory Layer. Au troisième, vous croyez l’outil. Au premier, vous avez une photo.",
    },
    {
      title: "Questions fréquentes",
      content: "Questions que se posent dirigeants, DAF et commerciaux après cet article. Réponses nettes.\n\nActivité peu saisonnière ?\n\nAlors cherchez d’autres patterns (clients, mix).\n\nMétéo / grève ?\n\nNotez-le. L’outil n’est pas un oracle météo.\n\nMulti-sites ?\n\nSaison × site. Sinon moyenne menteuse.\n\nPrévision ?\n\nFourchette qualitative. Pas de fausse décimale.",
      keyPoints: ["Activité peu saisonnière ? — Alors cherchez d’autres patterns (clients, mix).", "Météo / grève ? — Notez-le. L’outil n’est pas un oracle météo.", "Multi-sites ? — Saison × site. Sinon moyenne menteuse.", "Prévision ? — Fourchette qualitative. Pas de fausse décimale."],
    }
      ],
      conclusion: "La saison n’est pas une fatalité. C’est un pattern. Clairify s’en souvient. Vous arrêtez de l’improviser.",
      cta: {
        title: "Comparer deux saisons",
        description: "Deux exports de la même période, deux années.",
      },
    },
  },
  {
    id: "67",
    slug: "kpi-inutiles-vs-insights-actionnables",
    title: "KPI inutiles vs insights actionnables : ce qu’il faut arrêter de mesurer",
    metaTitle: "KPI inutiles vs insights actionnables | Clairify",
    metaDescription: "Trop de KPI tuent la décision. Comment passer des indicateurs vanity à des insights actionnables pour une PME.",
    excerpt: "Si tout est vert, rien n’est clair. Voici comment tuer les KPI qui occupent le comité — et garder ceux qui tranchent.",
    category: "Insights",
    readTime: 9,
    publishDate: "2026-09-06",
    author: { name: "Équipe Clairify", role: "Experts IA & Data" },
    keywords: ["KPI inutiles", "insights actionnables", "trop de KPI PME", "vanity metrics", "indicateurs décision"],
    content: {
      introduction: "Les entreprises collectionnent les KPI comme des trophées. Trafic, likes, lignes de pipeline, tickets clos, CA. Le comité dure deux heures. Personne ne décide. Un insight actionnable a un verbe : arrêter, hausser, relancer, cesser, recadrer.\n\nClairify n’ajoute pas une jauge. Il pousse des collisions. Si ça ne se traduit pas par un verbe, ce n’est pas un insight. C’est un KPI de plus.",
      sections: [
    {
      title: "Test du verbe",
      content: "Derrière chaque indicateur : quel verbe lundi ? Si la réponse est « surveiller », tuez-le ou reléguez-le au statutaire. Le surveiller sans owner est un hobby.",
      keyPoints: ["Verbe", "Owner", "Date"],
    },
    {
      title: "Les vanity les plus fréquents",
      content: "CA sans marge. Leads sans contribution. Taux d’ouverture. Nombre de rapports générés. NPS sans verbatim actionnable. Gardez-en un pour le board externe si vous voulez. Ne pilotez pas dessus.",
    },
    {
      title: "Tableau de bord mince",
      content: "Cinq indicateurs statutaires. Trois tensions Clairify. C’est un pack adulte. Le reste est du confort visuel.",
    },
    {
      title: "Questions fréquentes",
      content: "Questions que se posent dirigeants, DAF et commerciaux après cet article. Réponses nettes.\n\nLe board veut des graphes ?\n\nUne page statutaire + une page tensions. Deux métiers.\n\nISO / qualité ?\n\nLeurs KPI restent. Ne les mélangez pas au pilotage.\n\nStartup metrics (MRR…) ?\n\nUtile si vrai. Vanity si maquillé. Même test du verbe.\n\nQui tue un KPI ?\n\nLe dirigeant. Personne d’autre n’ose.",
      keyPoints: ["Le board veut des graphes ? — Une page statutaire + une page tensions. Deux métiers.", "ISO / qualité ? — Leurs KPI restent. Ne les mélangez pas au pilotage.", "Startup metrics (MRR…) ? — Utile si vrai. Vanity si maquillé. Même test du verbe.", "Qui tue un KPI ? — Le dirigeant. Personne d’autre n’ose."],
    }
      ],
      conclusion: "Moins de KPI, plus d’insights. C’est une discipline, pas un logiciel. Clairify aide à tenir la discipline.",
      cta: {
        title: "Remplacer un KPI par une tension",
        description: "Listez vos 20 KPI. Gardez 5. Importez le reste comme question.",
      },
    },
  },
  {
    id: "68",
    slug: "audit-marketing-roi-pme-ia",
    title: "Audit marketing PME : le ROI après contribution, pas après le clic",
    metaTitle: "Audit marketing ROI PME IA | Clairify",
    metaDescription: "Audit marketing : CAC, ROAS, contribution après retours et remise. Insights pour arrêter les canaux vanity.",
    excerpt: "Le marketing raconte des clics. L’entreprise vit de la contribution. L’audit, c’est le pont.",
    category: "Audit",
    readTime: 9,
    publishDate: "2026-09-07",
    author: { name: "Équipe Clairify", role: "Experts IA & Data" },
    keywords: ["audit marketing PME", "ROI marketing data", "ROAS vs contribution", "CAC réel", "performance canaux insights"],
    content: {
      introduction: "Un ROAS de 6 qui ignore les retours, la remise et le SAV n’est pas un ROI. C’est une slide. L’audit marketing d’une PME tient dans un croisement : coûts d’acquisition × commandes × marge × retours.\n\nLooker Studio raconte le trafic. Clairify raconte le business. Les deux. Pas l’un pour l’autre.",
      sections: [
    {
      title: "Périmètre honnête",
      content: "Inclure : média, outils, agence, promotions liées au canal. Exclure : la vie. Un audit qui oublie la promo « code influenceur » ment.",
      keyPoints: ["Coûts complets", "Promo", "Retours"],
    },
    {
      title: "Canaux qui s’annulent",
      content: "Le paid qui cannibalise l’organique. Le marketplace qui vole le site. Collision de canaux. Pattern sur deux saisons. Décision : budget, marque, marge.",
    },
    {
      title: "Livrable pour le dirigeant",
      content: "Trois canaux : on double, on tient, on coupe. Pas un rapport de 40 audiences. Le Memory Layer compare le mois suivant. Sinon l’audit était un one-shot d’agence.",
    },
    {
      title: "Questions fréquentes",
      content: "Questions que se posent dirigeants, DAF et commerciaux après cet article. Réponses nettes.\n\nGoogle / Meta ads ?\n\nCSV de coûts. Suffisant.\n\nAttribution last clic ?\n\nImparfaite. Quand même plus vraie qu’un vanity ROAS.\n\nMarque / brand ?\n\nNe tuez pas tout le brand. Isolez-le. Ne le faites pas porter le ROAS.\n\nPetite équipe marketing ?\n\nEncore plus besoin d’un audit court.",
      keyPoints: ["Google / Meta ads ? — CSV de coûts. Suffisant.", "Attribution last clic ? — Imparfaite. Quand même plus vraie qu’un vanity ROAS.", "Marque / brand ? — Ne tuez pas tout le brand. Isolez-le. Ne le faites pas porter le ROAS.", "Petite équipe marketing ? — Encore plus besoin d’un audit court."],
    }
      ],
      conclusion: "Auditer le marketing, c’est ramener le clic à la caisse. Clairify fait le pont. L’agence n’aime pas toujours. Tant mieux.",
      cta: {
        title: "Croiser pubs et ventes",
        description: "Export coûts + export commandes.",
      },
    },
  },
  {
    id: "69",
    slug: "eti-ia-metier-sans-equipe-data",
    title: "ETI sans équipe data : une IA métier au-dessus du SI, pas à la place",
    metaTitle: "ETI IA métier sans équipe data | Clairify",
    metaDescription: "ETI française sans data lab : comment une IA à mémoire clarifie les exports ERP/Excel sans projet BI de 18 mois.",
    excerpt: "Vous avez un ERP, des extractions, des comités. Vous n’avez pas six analystes. La couche décision peut exister quand même.",
    category: "Entreprise",
    readTime: 9,
    publishDate: "2026-09-08",
    author: { name: "Équipe Clairify", role: "Experts IA & Data" },
    keywords: ["IA ETI France", "ETI sans data analyst", "IA au-dessus ERP", "décision ETI data", "BI ETI sans projet"],
    content: {
      introduction: "L’ETI est coincée : trop grosse pour le feeling du gérant seul, trop « pas assez » pour un datalake à un million. Les projets BI s’enlisent. Les métiers exportent encore vers Excel. C’est exactement l’interstice Clairify : lire ces exports, mémoire, collisions, Deep Report de COMEX.\n\nOn ne remplace pas SAP. On clarifie ce que SAP ne dit pas en français de comité.",
      sections: [
    {
      title: "Architecture sobre",
      content: "ERP / compta / CRM restent. Extraction. Studio. Rituel. Droits. France. Si un jour l’équipe data arrive, elle hérite d’une mémoire, pas d’un cimetière de cubes.",
      keyPoints: ["Complément", "Pas un second SI"],
    },
    {
      title: "Gouvernance",
      content: "Un sponsor DAF ou DG. Des owners par tension. Pas 40 licences « pour voir ». Enterprise (199 €) si multi-users. Moins cher qu’un ETP analyste.",
    },
    {
      title: "Ce qui échoue",
      content: "Vouloir tout connecter en live. Vouloir un jumeau numérique. Commencer par un site, une BU, deux fichiers. Gagner. Étendre.",
    },
    {
      title: "Questions fréquentes",
      content: "Questions que se posent dirigeants, DAF et commerciaux après cet article. Réponses nettes.\n\nIT va bloquer ?\n\nDonnées en France, DPA, pas d’entraînement occulte. Dossier propre.\n\nUsines / MES ?\n\nExports qualité/prod si le cas le justifie. Pas un MES.\n\nFiliales ?\n\nUne mémoire par périmètre. Ne mélangez pas tout.\n\nComité de groupe ?\n\nDeep Report comme page « tensions BU ».",
      keyPoints: ["IT va bloquer ? — Données en France, DPA, pas d’entraînement occulte. Dossier propre.", "Usines / MES ? — Exports qualité/prod si le cas le justifie. Pas un MES.", "Filiales ? — Une mémoire par périmètre. Ne mélangez pas tout.", "Comité de groupe ? — Deep Report comme page « tensions BU »."],
    }
      ],
      conclusion: "L’ETI n’a pas besoin d’attendre le data lab. Elle a besoin d’une couche de clarté. C’est un étage, pas une révolution.",
      cta: {
        title: "Piloter une BU dans le studio",
        description: "Deux exports d’une BU, pas tout le groupe.",
      },
    },
  },
  {
    id: "70",
    slug: "expert-comptable-pilotage-ia-clients",
    title: "Expert-comptable : passer du bilan au pilotage (sans devenir data analyst)",
    metaTitle: "Expert-comptable pilotage IA clients | Clairify",
    metaDescription: "EC et cabinets : offrir du pilotage à vos clients PME. Insights, audit de marge, CA. Complément de la liasse, pas un concurrent.",
    excerpt: "La liasse dit le passé certifiable. Le client demande « et maintenant ? ». Une IA métier peut devenir votre couche de conversation.",
    category: "Entreprise",
    readTime: 9,
    publishDate: "2026-09-09",
    author: { name: "Équipe Clairify", role: "Experts IA & Data" },
    keywords: ["expert-comptable pilotage", "cabinet EC data", "conseil PME insights", "liasse vs pilotage", "EC IA France"],
    content: {
      introduction: "Les cabinets le savent : la déclaration ne suffit plus à retenir le client. Le conseil de gestion se vend mal s’il est artisanal (TCD le dimanche). Clairify n’est pas un outil de production comptable. C’est un studio que l’EC peut utiliser sur les exports déjà extraits — avec le cadre RGPD du cabinet.\n\nVous restez l’humain de confiance. L’IA prépare les tensions. Vous les politisez.",
      sections: [
    {
      title: "Offre possible",
      content: "Un rituel trimestriel : imports, Deep Report, une heure avec le dirigeant. Facturable. Différenciant. Moins risqué qu’un « pôle data » fantôme.",
      keyPoints: ["Trimestriel", "Pack", "Heure dirigeant"],
    },
    {
      title: "Frontière nette",
      content: "Pas de confusion avec la mission légale. Pas de mélange des corpus clients. Isolation. France. Mentions. Le client reste responsable de ses fichiers opérationnels.",
    },
    {
      title: "Ce que le client comprend",
      content: "« Votre CA monte, votre marge non, voici le client. » Phrase d’EC. L’outil accélère la phrase. La relation, c’est vous.",
    },
    {
      title: "Questions fréquentes",
      content: "Questions que se posent dirigeants, DAF et commerciaux après cet article. Réponses nettes.\n\nConcurrence Pennylane Conseil ?\n\nComplémentaire. Eux la collecte. Ici la collision.\n\nResponsabilité du conseil ?\n\nLa vôtre, comme aujourd’hui. L’IA n’est pas un expert-comptable.\n\nPetits clients ?\n\nCeux qui ont un Excel ventes. Pas uniquement les groupes.\n\nFormation associés ?\n\nUne heure. Le rituel fait le reste.",
      keyPoints: ["Concurrence Pennylane Conseil ? — Complémentaire. Eux la collecte. Ici la collision.", "Responsabilité du conseil ? — La vôtre, comme aujourd’hui. L’IA n’est pas un expert-comptable.", "Petits clients ? — Ceux qui ont un Excel ventes. Pas uniquement les groupes.", "Formation associés ? — Une heure. Le rituel fait le reste."],
    }
      ],
      conclusion: "Le pilotage est le conseil que la liasse ne porte pas. Clairify aide le cabinet à le tenir. Sans jouer à l’éditeur de compta.",
      cta: {
        title: "Tester sur un client volontaire",
        description: "Un export, un rendez-vous, un pack.",
      },
    },
  },
  {
    id: "71",
    slug: "directeur-commercial-insights-lundi-matin",
    title: "Directeur commercial : le lundi matin en une page (pipeline, mix, vrais comptes)",
    metaTitle: "Directeur commercial insights lundi | Clairify",
    metaDescription: "Dirco PME : insights du lundi — pipeline vs réel, mix, comptes qui pèsent. Une page, pas un CRM ouvert trois heures.",
    excerpt: "Le lundi du dirco se perd dans le CRM. Une page d’insights rend l’équipe plus nette que vingt filtres.",
    category: "Pilotage",
    readTime: 9,
    publishDate: "2026-09-10",
    author: { name: "Équipe Clairify", role: "Experts IA & Data" },
    keywords: ["directeur commercial insights", "lundi commercial data", "pipeline vs réel", "animation commerciale data", "dirco PME IA"],
    content: {
      introduction: "Le directeur commercial n’a pas besoin d’un énième dashboard CRM. Il a besoin de trois phrases pour l’équipe : voici les comptes qui portent, voici les deals zombies, voici le mix que l’on pousse trop. Ça sort d’un croisement, pas d’un feeling de vendredi.\n\nClairify prépare la page. L’animation, c’est vous. Incentives, coaching, terrain : l’humain. Clarté : la machine.",
      sections: [
    {
      title: "La page du lundi",
      content: "Concentration. Écart forecast/facturé. Collision mix/marge si vous l’avez. Une relance prioritaire. C’est tout. Le reste est du bruit de pipeline.",
      keyPoints: ["Concentration", "Zombies", "Mix"],
    },
    {
      title: "Ne pas fliquer",
      content: "Si la page devient un ranking humiliant, l’équipe sabotera la saisie. Politique, pas tribunal. Les insights protègent ceux qui portent vraiment.",
    },
    {
      title: "Rituel avec le DAF",
      content: "Quinze minutes. Cash vs signé. Une guerre évitée. Le Memory Layer compare la semaine 12 à la semaine 4. Vous n’êtes plus seul à « vous souvenir ».",
    },
    {
      title: "Questions fréquentes",
      content: "Questions que se posent dirigeants, DAF et commerciaux après cet article. Réponses nettes.\n\nÉquipe de 3 ou de 25 ?\n\nMême page. Granularité différente.\n\nPas de CRM ?\n\nL’export ventes suffit pour concentration et mix.\n\nChallenges du mois ?\n\nAlignez-les sur la contribution, pas le volume.\n\nGrand compte unique ?\n\nL’insight sera politique. Il faut le voir quand même.",
      keyPoints: ["Équipe de 3 ou de 25 ? — Même page. Granularité différente.", "Pas de CRM ? — L’export ventes suffit pour concentration et mix.", "Challenges du mois ? — Alignez-les sur la contribution, pas le volume.", "Grand compte unique ? — L’insight sera politique. Il faut le voir quand même."],
    }
      ],
      conclusion: "Un dirco clair gagne du terrain, pas des réunions. Une page. Une équipe. Clairify.",
      cta: {
        title: "Préparer le prochain lundi",
        description: "CRM + ventes. Dimanche soir ou lundi 8 h.",
      },
    },
  },
  {
    id: "72",
    slug: "controle-de-gestion-ia-pme-sans-cube",
    title: "Contrôle de gestion en PME : l’IA à la place du cube (pour commencer)",
    metaTitle: "Contrôle de gestion IA PME | Clairify",
    metaDescription: "Contrôle de gestion sans hypercube : écarts, marge, centres. Une IA métier pour PME qui n’auront jamais un contrôleur à temps plein.",
    excerpt: "Le contrôle de gestion, c’est des écarts et des questions. Pas forcément un logiciel de 50 k€ et un FTE.",
    category: "Pilotage",
    readTime: 9,
    publishDate: "2026-09-11",
    author: { name: "Équipe Clairify", role: "Experts IA & Data" },
    keywords: ["contrôle de gestion PME", "contrôleur de gestion IA", "écarts budget réel", "pilotage budgétaire PME", "CdG sans BI"],
    content: {
      introduction: "Beaucoup de PME font du contrôle de gestion sans le nommer : le DAF, le gérant, un Excel. L’écart budget/réel arrive trop tard. Les commentaires se recopient. Une IA à mémoire n’est pas un CdG certifié. Elle accélère l’écart et la collision.\n\nBudget vs réel, centre vs centre, N vs N-1. Deep Report = commentaires de CdG en brouillon. Vous signez.",
      sections: [
    {
      title: "Le minimum d’un CdG",
      content: "Un budget (même simple). Un réel. Des écarts. Des causes. Une action. Si vous n’avez pas de budget, commencez par N vs N-1. C’est déjà du contrôle.",
      keyPoints: ["Budget", "Réel", "Cause", "Action"],
    },
    {
      title: "Ce que l’IA ne doit pas inventer",
      content: "Des causes romancées. « C’est le marché. » Elle doit pointer le fichier. Vous écrivez la cause humaine.",
    },
    {
      title: "Quand embaucher un vrai CdG",
      content: "Multi-BU, reporting groupe, crédits, seuils. Avant ça, un studio + un DAF lucide. Clairify assume ce palier.",
    },
    {
      title: "Questions fréquentes",
      content: "Questions que se posent dirigeants, DAF et commerciaux après cet article. Réponses nettes.\n\nPas de budget formel ?\n\nN-1. Ou un budget napkin. Mieux que rien.\n\nCentres de coûts ?\n\nSi l’export les a. Sinon par nature / famille.\n\nMensuel ?\n\nOui. Le CdG annuel est une messe, pas un pilotage.\n\nLien Deep Report ?\n\nC’est le commentaire d’écarts, pas la liasse.",
      keyPoints: ["Pas de budget formel ? — N-1. Ou un budget napkin. Mieux que rien.", "Centres de coûts ? — Si l’export les a. Sinon par nature / famille.", "Mensuel ? — Oui. Le CdG annuel est une messe, pas un pilotage.", "Lien Deep Report ? — C’est le commentaire d’écarts, pas la liasse."],
    }
      ],
      conclusion: "Le contrôle de gestion, c’est une phrase d’écart. Pas un cube. Clairify écrit le brouillon. Le DAF assume.",
      cta: {
        title: "Importer budget et réel",
        description: "Deux onglets. Un écart. Un owner.",
      },
    },
  },
  {
    id: "73",
    slug: "fuites-chiffre-affaires-detecter-ia",
    title: "Fuites de chiffre d’affaires : retours, avoirs, churn, sous-tarifs — les voir",
    metaTitle: "Fuites chiffre d’affaires détecter IA | Clairify",
    metaDescription: "Détecter les fuites de CA : retours, avoirs, clients perdus, remises. Audit de croissance négative pour PME.",
    excerpt: "On parle toujours d’augmenter le CA. On parle trop peu de ce qui fuit. Les fuites sont dans les fichiers. Elles ont un nom.",
    category: "Croissance",
    readTime: 9,
    publishDate: "2026-09-12",
    author: { name: "Équipe Clairify", role: "Experts IA & Data" },
    keywords: ["fuites chiffre d'affaires", "détecter churn PME", "avoirs retours data", "CA perdu insights", "leakage revenue"],
    content: {
      introduction: "Une fuite, c’est du CA qui aurait dû rester. Retours e-commerce. Avoirs. Clients silencieux. Remise devenue prix. Projet non refacturé. Chaque fuite a un export. Ensemble, elles expliquent pourquoi « on bosse plus » sans grandir.\n\nClairify ne bouche pas les fuites. Il les cartographie. Le reste est de l’ops et du commercial.",
      sections: [
    {
      title: "Inventaire des fuites",
      content: "Retours. Avoirs. Impayés. Churn. Non-réachat. Heures perdues. Cannibalisation de canaux. Choisissez-en deux pour l’audit. Pas sept.",
      keyPoints: ["Retours", "Churn", "Remise", "Non-refacturé"],
    },
    {
      title: "Fuite vs investissement",
      content: "Un SAV qui fidélise n’est pas une fuite. Un SAV qui répare une offre cassée, si. L’insight doit qualifier. L’IA propose. Vous tranchez.",
    },
    {
      title: "Indicateur unique interdit",
      content: "« Taux de retour 8 % » ne dit rien. Retour × SKU × canal × marge, si. Collision. Mémoire saisonnière.",
    },
    {
      title: "Questions fréquentes",
      content: "Questions que se posent dirigeants, DAF et commerciaux après cet article. Réponses nettes.\n\nChurn B2B comment ?\n\nNon-réachat sur une fenêtre. Simple et dur.\n\nServices / régie ?\n\nHeures non facturées = fuite reine.\n\nOn les connaissait déjà ?\n\nAlors l’outil sert à les tenir à 15 jours, pas à « découvrir ».\n\nPartager à l’équipe ?\n\nLes fuites, oui. Les noms clients, cercle restreint.",
      keyPoints: ["Churn B2B comment ? — Non-réachat sur une fenêtre. Simple et dur.", "Services / régie ? — Heures non facturées = fuite reine.", "On les connaissait déjà ? — Alors l’outil sert à les tenir à 15 jours, pas à « découvrir ».", "Partager à l’équipe ? — Les fuites, oui. Les noms clients, cercle restreint."],
    }
      ],
      conclusion: "Boucher une fuite est souvent le CA le moins cher à « gagner ». Encore faut-il la voir. C’est le job.",
      cta: {
        title: "Cartographier deux fuites",
        description: "Avoirs + ventes, ou retours + commandes.",
      },
    },
  },
  {
    id: "74",
    slug: "analyser-historique-ventes-entreprise-ia",
    title: "Analyser l’historique des ventes : ce que 24 mois racontent vraiment",
    metaTitle: "Analyser historique ventes entreprise IA | Clairify",
    metaDescription: "Analyse d’historique des ventes PME : 12–24 mois, mix, clients, saison. Insights et patterns, sans data scientist.",
    excerpt: "L’historique n’est pas un archive mort. C’est le seul endroit où les patterns existent. Encore faut-il le lire comme une histoire, pas comme un TCD.",
    category: "Insights",
    readTime: 9,
    publishDate: "2026-09-13",
    author: { name: "Équipe Clairify", role: "Experts IA & Data" },
    keywords: ["analyser historique ventes", "historique CA entreprise", "analyse ventes 24 mois", "tendances ventes PME", "historique commercial data"],
    content: {
      introduction: "On ouvre l’année en cours. On ignore N-1. On s’étonne. L’historique des ventes, lu avec mémoire, dit le mix qui glisse, le client qui s’éteint, la saison, la remise qui s’installe. 24 mois battent 24 dashboards du mois.\n\nClairify indexe cet historique. Un fichier long et propre vaut mieux que dix exports hebdo incohérents.",
      sections: [
    {
      title: "Préparer 24 mois",
      content: "Même colonnes. Dates. Clients. Familles. HT. Pas de totaux dans les lignes. Si l’ERP change de code produit, documentez. Sinon faux patterns.",
      keyPoints: ["Schéma stable", "HT", "Identifiants"],
    },
    {
      title: "Questions à poser à l’historique",
      content: "Qui monte, qui descend, qui disparaît. Quelles familles. Quelle concentration. Quelle saison. Quelle remise. C’est un audit de CA rétroactif.",
    },
    {
      title: "Ne pas sur-interpréter",
      content: "Un COVID, un départ de commercial, un arrêt produit. Notez les ruptures. L’IA n’était pas dans l’entreprise. Vous si.",
    },
    {
      title: "Questions fréquentes",
      content: "Questions que se posent dirigeants, DAF et commerciaux après cet article. Réponses nettes.\n\nOn n’a que 6 mois ?\n\nPartez. La mémoire grandira.\n\nDeux ERP dans la période ?\n\nHarmonisez les clés. Ou deux périodes séparées.\n\nB2C énorme volume ?\n\nAgrégats journaliers / SKU. Pas forcément chaque ticket nominatif.\n\nLien prévision ?\n\nL’historique borne. Il ne garantit pas demain.",
      keyPoints: ["On n’a que 6 mois ? — Partez. La mémoire grandira.", "Deux ERP dans la période ? — Harmonisez les clés. Ou deux périodes séparées.", "B2C énorme volume ? — Agrégats journaliers / SKU. Pas forcément chaque ticket nominatif.", "Lien prévision ? — L’historique borne. Il ne garantit pas demain."],
    }
      ],
      conclusion: "L’historique est le capital data d’une PME. Le lire, c’est déjà augmenter les insights. Clairify s’en souvient pour les mois suivants.",
      cta: {
        title: "Importer 24 mois de ventes",
        description: "Un export long. Un premier Deep Report.",
      },
    },
  },
  {
    id: "75",
    slug: "digitalisation-decision-entreprise-ia",
    title: "Digitaliser les décisions de l’entreprise (pas seulement les factures)",
    metaTitle: "Digitalisation décisions entreprise IA | Clairify",
    metaDescription: "Digitalisation PME : au-delà de la facture électronique, digitaliser le pilotage. Insights, audit, mémoire. IA France.",
    excerpt: "La digitalisation a souvent arrêté à la facture et au cloud. Les décisions, elles, sont encore orales. C’est le prochain étage.",
    category: "Entreprise",
    readTime: 9,
    publishDate: "2026-09-14",
    author: { name: "Équipe Clairify", role: "Experts IA & Data" },
    keywords: ["digitalisation entreprise PME", "digitaliser décisions", "transformation data PME", "pilotage digital IA", "entreprise data driven"],
    content: {
      introduction: "On a digitalisé la collecte (compta, caisse, CRM). On n’a pas digitalisé l’arbitrage. Les décisions restent dans une salle, un feeling, un WhatsApp. Digitaliser la décision, c’est écrire la tension, la date, l’owner, et s’en souvenir.\n\nClairify est cet étage : pas un nouvel ERP. Une mémoire de ce que les outils opérationnels ont déjà capturé.",
      sections: [
    {
      title: "Ce qui est déjà digital (et inutilisé)",
      content: "Exports partout. PDF partout. Personne au croisement. La digitalisation sans lecture, c’est de la poussière dans le cloud.",
      keyPoints: ["Collecte", "Lecture", "Mémoire"],
    },
    {
      title: "Le rituel digital de décision",
      content: "Import. Collision. Owner. Revue 15 jours. C’est peu d’outils. C’est beaucoup de discipline. L’IA rend la discipline tenable.",
    },
    {
      title: "Changement humain",
      content: "Certains préfèrent le flou : il protège. Clarifier, c’est politique. Le dirigeant doit vouloir. Sinon aucun logiciel n’y fera rien.",
    },
    {
      title: "Questions fréquentes",
      content: "Questions que se posent dirigeants, DAF et commerciaux après cet article. Réponses nettes.\n\nFacture électronique = digitalisé ?\n\nCollecte, pas décision.\n\nWhatsApp de direction ?\n\nOral + oubli. Le contraire d’une mémoire.\n\nConduite du changement ?\n\nUn rituel court bat un séminaire.\n\nTous les métiers ?\n\nUn métier d’abord. Puis le suivant.",
      keyPoints: ["Facture électronique = digitalisé ? — Collecte, pas décision.", "WhatsApp de direction ? — Oral + oubli. Le contraire d’une mémoire.", "Conduite du changement ? — Un rituel court bat un séminaire.", "Tous les métiers ? — Un métier d’abord. Puis le suivant."],
    }
      ],
      conclusion: "Digitaliser l’entreprise, au fond, c’est digitaliser ce qu’on tranche. Le reste est de l’intendance. Clairify tient l’intendance de la clarté.",
      cta: {
        title: "Digitaliser une première décision",
        description: "Une tension écrite cette semaine.",
      },
    },
  },
  {
    id: "76",
    slug: "data-driven-entreprise-par-ou-commencer",
    title: "Devenir data-driven : par où commencer vraiment (PME, 30 jours)",
    metaTitle: "Devenir data-driven PME 30 jours | Clairify",
    metaDescription: "PME data-driven : plan 30 jours. Fichiers, rituel, insights, audit d’une zone. Sans projet BI, sans buzzword.",
    excerpt: "Data-driven n’est pas un état mystique. C’est un rituel de 30 jours. Voici le plan, sans théâtre.",
    category: "Méthode",
    readTime: 9,
    publishDate: "2026-09-15",
    author: { name: "Équipe Clairify", role: "Experts IA & Data" },
    keywords: ["devenir data driven PME", "par où commencer data", "plan data 30 jours", "culture data entreprise", "premiers pas data driven"],
    content: {
      introduction: "« On veut être data-driven » orne les vœux. En janvier, Excel a repris. Le plan réaliste : une zone, deux fichiers, un owner, quatre imports, trois décisions. Si ça ne tient pas 30 jours, aucun datalake n’y fera rien.\n\nClairify est calé pour ce mois. Gratuit pour tester. Memory Layer pour que le mois 2 ne soit pas le mois 1.",
      sections: [
    {
      title: "Semaine 1 — une zone",
      content: "Ventes, ou marge, ou cash. Pas les trois. Export. Import. Première lecture. Une question écrite.",
      keyPoints: ["Une zone", "Un owner"],
    },
    {
      title: "Semaines 2–3 — le croisement",
      content: "Deuxième source. Collision. Owner. Date. C’est le cœur du « data-driven » : une tension tenue.",
    },
    {
      title: "Semaine 4 — le comité",
      content: "Deep Report. Trois verbes. Décider si le rituel survit. Si oui, vous êtes plus data-driven que 80 % des slides du secteur. Si non, vous avez appris que ce n’était pas le moment — c’est déjà une donnée.",
    },
    {
      title: "Questions fréquentes",
      content: "Questions que se posent dirigeants, DAF et commerciaux après cet article. Réponses nettes.\n\nQui owner ?\n\nDAF ou DG. Pas « le stagiaire data » seul.\n\nEt la culture d’équipe ?\n\nElle suit les décisions tenues, pas les affiches.\n\nOutils déjà là ?\n\nGardez-les. Ajoutez la lecture.\n\nÉchec du mois 1 ?\n\nSouvent fichiers sales ou pas d’owner. Corrigez, ne fuyez pas.",
      keyPoints: ["Qui owner ? — DAF ou DG. Pas « le stagiaire data » seul.", "Et la culture d’équipe ? — Elle suit les décisions tenues, pas les affiches.", "Outils déjà là ? — Gardez-les. Ajoutez la lecture.", "Échec du mois 1 ? — Souvent fichiers sales ou pas d’owner. Corrigez, ne fuyez pas."],
    }
      ],
      conclusion: "On ne devient pas data-driven. On tient un rituel. Clairify rend le rituel possible. Vous le tenez.",
      cta: {
        title: "Démarrer les 30 jours",
        description: "Aujourd’hui : choisir la zone. Demain : l’export.",
      },
    },
  },
  {
    id: "77",
    slug: "reunions-direction-insights-inutiles",
    title: "Réunions de direction inutiles : quand l’absence d’insights coûte un mercredi",
    metaTitle: "Réunions direction insights inutiles | Clairify",
    metaDescription: "CODIR trop longs : trop de KPI, pas d’insights. Comment une page de collisions raccourcit la réunion et augmente les décisions.",
    excerpt: "Le mercredi de direction n’est pas une fatalité. Il est le symptôme d’un manque de clarté en amont.",
    category: "Pilotage",
    readTime: 9,
    publishDate: "2026-09-16",
    author: { name: "Équipe Clairify", role: "Experts IA & Data" },
    keywords: ["réunions direction trop longues", "CODIR efficace data", "ordre du jour insights", "réunion inutile PME", "COMEX une page"],
    content: {
      introduction: "Deux heures, vingt sujets, zéro décision. Coût : salaires autour de la table, opportunité, moral. La cause n’est pas « on ne sait pas animer ». C’est : on n’a pas clarifié avant. On découvre les chiffres ensemble. On débat de souvenirs divergents.\n\nUne page Clairify J-1 change l’ordre du jour : on tranche des tensions déjà écrites. On ne reconstitue pas le monde.",
      sections: [
    {
      title: "Avant : le pack",
      content: "Deep Report. Trois tensions. Chacun a lu. Sinon on décale. Impitoyable et salutaire.",
      keyPoints: ["J-1", "Lecture", "3 tensions"],
    },
    {
      title: "Pendant : verbes",
      content: "Arrêter, hausser, relancer, cesser. Le tour de table « et toi, tu en penses quoi ? » sans insight est un luxe.",
    },
    {
      title: "Après : 15 jours",
      content: "La réunion sans revue est un spectacle. La mémoire comparera. C’est le seul KPI de réunion qui compte : décisions tenues.",
    },
    {
      title: "Questions fréquentes",
      content: "Questions que se posent dirigeants, DAF et commerciaux après cet article. Réponses nettes.\n\nEt le social / RH dans la réunion ?\n\nGardez un créneau. Ne le mélangez pas aux chiffres flous.\n\nParticipants ?\n\nCeux qui tranchent. Les autres reçoivent la page.\n\nHebdo trop fréquent ?\n\nOui souvent. Mensuel + exceptions cash.\n\nRésistance « on a toujours fait comme ça » ?\n\nMontrez une réunion de 45 minutes. L’habitude suit.",
      keyPoints: ["Et le social / RH dans la réunion ? — Gardez un créneau. Ne le mélangez pas aux chiffres flous.", "Participants ? — Ceux qui tranchent. Les autres reçoivent la page.", "Hebdo trop fréquent ? — Oui souvent. Mensuel + exceptions cash.", "Résistance « on a toujours fait comme ça » ? — Montrez une réunion de 45 minutes. L’habitude suit."],
    }
      ],
      conclusion: "Raccourcir les réunions, c’est augmenter la capacité de l’entreprise. Pas un icebreaker. De la clarté en amont.",
      cta: {
        title: "Préparer le prochain CODIR",
        description: "Un Deep Report. Une heure, pas deux.",
      },
    },
  },
  {
    id: "78",
    slug: "performance-commerciale-patterns-ia",
    title: "Performance commerciale : les patterns qui séparent un bon mois d’un mois qui paie",
    metaTitle: "Performance commerciale patterns IA | Clairify",
    metaDescription: "Patterns de performance commerciale : mix, cycle, concentration, discount. Lire l’équipe autrement que par le CA brut.",
    excerpt: "Le meilleur mois n’est pas toujours le plus gros CA. Les patterns de performance le montrent — si on les cherche.",
    category: "Patterns",
    readTime: 9,
    publishDate: "2026-09-17",
    author: { name: "Équipe Clairify", role: "Experts IA & Data" },
    keywords: ["performance commerciale PME", "patterns ventes équipe", "analyse performance commerciale", "mix commercial data", "cycle de vente insights"],
    content: {
      introduction: "On classe les commerciaux au CA. On ignore le mix, le DSO, le discount, le taux de transformation réel (facturé, pas « gagné CRM »). Un pattern de performance, c’est une habitude qui paie vraiment : tel type d’affaires, tel cycle, tel panier.\n\nClairify croise CRM et factures. L’animation devient plus juste. Moins de héros du discount. Plus de répétabilité.",
      sections: [
    {
      title: "CA vs contribution vs cash",
      content: "Trois classements, trois héros différents. L’insight, c’est de les poser sur la même page. Collision interne à l’équipe, parfois. Politique. Nécessaire.",
      keyPoints: ["CA", "Marge", "Encaissé"],
    },
    {
      title: "Répétabilité",
      content: "Un crack imprévisible n’est pas un système. Un pattern reproductible, si. Formez sur le pattern, pas sur la légende.",
    },
    {
      title: "Incentives",
      content: "Si la prime suit le volume, les patterns suivront le volume. Alignez. L’outil ne remplace pas la politique de paie. Il la met à nu.",
    },
    {
      title: "Questions fréquentes",
      content: "Questions que se posent dirigeants, DAF et commerciaux après cet article. Réponses nettes.\n\nPetite équipe, ça stigmatise ?\n\nParlez patterns, pas personnes, d’abord.\n\nNouveau commercial ?\n\nPas assez d’historique. Ne l’incluez pas au ranking.\n\nGrands comptes vs volume ?\n\nDeux patterns. Deux animations.\n\nLien formation ?\n\nFormez sur le mix qui contribue. Pas sur le pitch générique.",
      keyPoints: ["Petite équipe, ça stigmatise ? — Parlez patterns, pas personnes, d’abord.", "Nouveau commercial ? — Pas assez d’historique. Ne l’incluez pas au ranking.", "Grands comptes vs volume ? — Deux patterns. Deux animations.", "Lien formation ? — Formez sur le mix qui contribue. Pas sur le pitch générique."],
    }
      ],
      conclusion: "La performance commerciale se lit en patterns, pas en podium. Clairify écrit le tableau. Vous changez les règles — ou pas.",
      cta: {
        title: "Croiser activité et réel",
        description: "Un mois de CRM + un mois de factures.",
      },
    },
  },
  {
    id: "79",
    slug: "ca-qui-monte-marge-qui-baisse-collision",
    title: "Le CA monte, la marge baisse : la collision la plus fréquente en PME",
    metaTitle: "CA qui monte marge qui baisse | Clairify",
    metaDescription: "CA en hausse, marge en baisse : causes, audit, décisions. Pattern collision promo/mix/prix. Guide dirigeant et DAF.",
    excerpt: "C’est LA phrase que Clairify a été créé pour écrire. Voici comment la traiter sans se raconter d’histoires.",
    category: "Audit",
    readTime: 9,
    publishDate: "2026-09-18",
    author: { name: "Équipe Clairify", role: "Experts IA & Data" },
    keywords: ["CA hausse marge baisse", "collision CA marge", "promo tue marge", "mix destructeur", "croissance non rentable"],
    content: {
      introduction: "Le comité applaudit le CA. Le DAF regarde la contribution. Silence. Cette collision est partout : promo, mix entrée de gamme, heures non facturées, acquisition chère, discount commercial. Elle n’est pas une fatalité de « l’inflation » — parfois si, souvent non.\n\nUn Pattern Collision Clairify cite les deux sources. Owner. 15 jours. Soit on tient une règle (seuil de marge, SKU, remise), soit on assume de grandir pauvre. Les deux sont des décisions. Le flou n’en est pas une.",
      sections: [
    {
      title: "Causes à tester dans l’ordre",
      content: "Remises. Mix. Coût d’achat / matière. Volume vs prix. Canal. Charge de SAV. Ne jurez pas « c’est le marché » avant d’avoir ouvert les deux fichiers.",
      keyPoints: ["Remise", "Mix", "Coûts", "Canal"],
    },
    {
      title: "Règle de sortie",
      content: "Un seuil. Un responsable. Une date de fin de « test ». La mémoire dira si la collision s’aggrave. C’est le rituel.",
    },
    {
      title: "Communication interne",
      content: "Ne dites pas « les ventes ont mal travaillé ». Dites « deux vérités ». Le commercial a souvent raison sur le volume. Le DAF sur la contribution. L’entreprise doit choisir.",
    },
    {
      title: "Questions fréquentes",
      content: "Questions que se posent dirigeants, DAF et commerciaux après cet article. Réponses nettes.\n\nSans compta analytique ?\n\nRemises et mix déjà. Les coûts viendront.\n\nInflation réelle ?\n\nIsolez-la. Le reste est du pilotage.\n\nOn a besoin du volume ?\n\nAlors dites-le. Prix d’entrée assumé, pas subi.\n\nTous les mois ?\n\nTant que la collision est ouverte. Oui.",
      keyPoints: ["Sans compta analytique ? — Remises et mix déjà. Les coûts viendront.", "Inflation réelle ? — Isolez-la. Le reste est du pilotage.", "On a besoin du volume ? — Alors dites-le. Prix d’entrée assumé, pas subi.", "Tous les mois ? — Tant que la collision est ouverte. Oui."],
    }
      ],
      conclusion: "CA qui monte, marge qui baisse : le cas d’école. Si vous n’en traitez qu’une cette année, que ce soit celle-là.",
      cta: {
        title: "Nommer votre collision CA/marge",
        description: "Ventes + marge. Une page. Un owner.",
      },
    },
  },
  {
    id: "80",
    slug: "clarifier-donnees-augmenter-resultats-entreprise",
    title: "Clarifier vos données pour augmenter vos résultats (le contrat Clairify)",
    metaTitle: "Clarifier données augmenter résultats | Clairify",
    metaDescription: "Clarifier les données d’entreprise pour augmenter CA, marge, insights. Audit, patterns, mémoire. Manifeste et méthode PME / ETI.",
    excerpt: "Augmenter les résultats sans clarifier, c’est accélérer dans le brouillard. Voici le contrat, sans magie.",
    category: "Croissance",
    readTime: 9,
    publishDate: "2026-09-19",
    author: { name: "Équipe Clairify", role: "Experts IA & Data" },
    keywords: ["clarifier données entreprise", "augmenter résultats data", "IA clarifier business", "résultats PME insights", "Clairify méthode"],
    content: {
      introduction: "Clairify, c’est un verbe avant d’être un logiciel : clarifier. Les résultats (CA, marge, cash, temps de comité) viennent après, si quelqu’un tranche. Nous ne vendons pas une fusée de croissance. Nous vendons la fin du brouillard — Memory Layer, collisions, Deep Report, France.\n\nCe journal entier (audit, insights, patterns, métiers) ne dit que ça. L’article 80 referme la boucle : pour qui, pour quoi, comment, et ce que nous ne promettrons jamais.",
      sections: [
    {
      title: "Pour qui",
      content: "Dirigeant, DAF, dirco, ops de PME et d’ETI sans usine à data. France, RGPD, fichiers réels. Pas un data lab. Pas un chatbot de rédaction.",
      keyPoints: ["DG", "DAF", "Dirco", "Ops"],
    },
    {
      title: "Pour quoi",
      content: "Auditer une zone. Augmenter les insights utiles. Voir le CA vrai (contribution, fuites). Détecter les patterns. Décider en comité. Raccourcir le mercredi.",
    },
    {
      title: "Comment, concrètement",
      content: "Export. Import. Collision. Owner. Revue. Deep Report. Tokens (chat 8–10k, rapport 20–30k). Gratuit 50k pour voir. Pro 29 € quand le rituel existe.",
    },
    {
      title: "Ce que nous ne promettons pas",
      content: "Pas +40 % de CA magiques. Pas 98 % de prévision. Pas le remplacement de votre jugement. Si quelqu’un vous le promet, ce n’est pas de la clarté. C’est de la pub.",
    },
    {
      title: "Questions fréquentes",
      content: "Questions que se posent dirigeants, DAF et commerciaux après cet article. Réponses nettes.\n\nPar quel article commencer ?\n\nAudit données, ou CA/marge, ou le guide Excel. Puis un import.\n\nCombien d’articles lire ?\n\nPeu. Un fichier bat dix lectures.\n\nOn est trop petits ?\n\nUn artisan avec un suivi a déjà assez.\n\nOn est trop gros ?\n\nUne BU d’abord. L’ETI peut commencer étroit.",
      keyPoints: ["Par quel article commencer ? — Audit données, ou CA/marge, ou le guide Excel. Puis un import.", "Combien d’articles lire ? — Peu. Un fichier bat dix lectures.", "On est trop petits ? — Un artisan avec un suivi a déjà assez.", "On est trop gros ? — Une BU d’abord. L’ETI peut commencer étroit."],
    }
      ],
      conclusion: "Clarifier, puis augmenter. Dans cet ordre. Vos fichiers savent déjà. Le studio est là pour qu’ils parlent enfin — et que vous tranchiez.",
      cta: {
        title: "Clarifier, maintenant",
        description: "Sans carte. 50 000 tokens. Hébergé à Paris.",
      },
    },
  }
];
